'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Link } from '@/core/i18n/navigation';
import { LazyImage } from '@/shared/blocks/common';
import { SmartIcon } from '@/shared/blocks/common/smart-icon';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/utils';
import { Section } from '@/shared/types/blocks/landing';
export function ShowcasesFlow(t0) {
  const $ = _c(57);
  const {
    section,
    className
  } = t0;
  let t1;
  if ($[0] !== section) {
    t1 = (section as any).groups || [];
    $[0] = section;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const groups = t1;
  const [selectedGroup, setSelectedGroup] = useState(groups.length > 0 ? groups[0].name : "");
  const [selectedIndex, setSelectedIndex] = useState(null);
  let t2;
  bb0: {
    if (!section.items) {
      let t3;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [];
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      t2 = t3;
      break bb0;
    }
    if (!selectedGroup || !groups.length) {
      t2 = section.items;
      break bb0;
    }
    if (selectedGroup === "all") {
      t2 = section.items;
      break bb0;
    }
    let t3;
    if ($[3] !== section.items || $[4] !== selectedGroup) {
      let t4;
      if ($[6] !== selectedGroup) {
        t4 = item => item.group === selectedGroup;
        $[6] = selectedGroup;
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      t3 = section.items.filter(t4);
      $[3] = section.items;
      $[4] = selectedGroup;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = t3;
  }
  const filteredItems = t2;
  let t3;
  if ($[8] !== filteredItems.length) {
    t3 = () => {
      setSelectedIndex(prev => prev !== null ? prev === 0 ? (filteredItems.length ?? 1) - 1 : prev - 1 : null);
    };
    $[8] = filteredItems.length;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handlePrevious = t3;
  let t4;
  if ($[10] !== filteredItems.length) {
    t4 = () => {
      setSelectedIndex(prev_0 => prev_0 !== null ? prev_0 === (filteredItems.length ?? 1) - 1 ? 0 : prev_0 + 1 : null);
    };
    $[10] = filteredItems.length;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleNext = t4;
  let t5;
  let t6;
  if ($[12] !== handleNext || $[13] !== handlePrevious || $[14] !== selectedIndex) {
    t5 = () => {
      const handleKeyDown = e => {
        if (selectedIndex === null) {
          return;
        }
        if (e.key === "Escape") {
          setSelectedIndex(null);
        }
        if (e.key === "ArrowLeft") {
          handlePrevious();
        }
        if (e.key === "ArrowRight") {
          handleNext();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t6 = [selectedIndex, handlePrevious, handleNext];
    $[12] = handleNext;
    $[13] = handlePrevious;
    $[14] = selectedIndex;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  const t7 = section.id || section.name;
  let t8;
  if ($[17] !== className || $[18] !== section.className) {
    t8 = cn("py-24 md:py-36", section.className, className);
    $[17] = className;
    $[18] = section.className;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t10;
  let t11;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      opacity: 0,
      y: 20
    };
    t10 = {
      opacity: 1,
      y: 0
    };
    t11 = {
      once: true
    };
    $[20] = t10;
    $[21] = t11;
    $[22] = t9;
  } else {
    t10 = $[20];
    t11 = $[21];
    t9 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [0.22, 1, 0.36, 1];
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      duration: 0.6,
      ease: t12 as const
    };
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== section.sr_only_title) {
    t14 = section.sr_only_title && <h1 className="sr-only">{section.sr_only_title}</h1>;
    $[25] = section.sr_only_title;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== section.title) {
    t15 = <h2 className="mx-auto mb-6 max-w-full text-3xl font-bold text-pretty md:max-w-5xl lg:text-4xl">{section.title}</h2>;
    $[27] = section.title;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== section.description) {
    t16 = <p className="text-muted-foreground text-md mx-auto mb-4 line-clamp-3 max-w-full md:max-w-5xl">{section.description}</p>;
    $[29] = section.description;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== section.buttons) {
    t17 = section.buttons && section.buttons.length > 0 && <div className="container mx-auto mt-8 mb-12 flex flex-wrap justify-center gap-4">{section.buttons.map(_temp)}</div>;
    $[31] = section.buttons;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t14 || $[34] !== t15 || $[35] !== t16 || $[36] !== t17) {
    t18 = <motion.div className="container mb-12 text-center" initial={t9} whileInView={t10} viewport={t11} transition={t13}>{t14}{t15}{t16}{t17}</motion.div>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== groups || $[39] !== selectedGroup) {
    t19 = groups.length > 0 && <motion.div className="container mb-12 flex flex-wrap justify-center gap-4" initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: 0.15,
      ease: [0.22, 1, 0.36, 1] as const
    }}>{groups.map((group, index) => {
        const isSelected = selectedGroup === group.name;
        return <motion.button key={group.name} onClick={() => setSelectedGroup(group.name)} className={cn("relative rounded-lg px-3 py-1.5 text-sm font-medium transition-all", isSelected ? "" : "border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground border")} initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: 0.2 + index * 0.1,
          ease: [0.22, 1, 0.36, 1] as const
        }} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }}>{isSelected ? <><span className="bg-primary absolute inset-0 rounded-lg p-[2px]"><span className="bg-background block h-full w-full rounded-[calc(0.5rem-2px)]" /></span><span className="bg-primary relative z-10 bg-clip-text text-transparent">{group.title}</span></> : <span>{group.title}</span>}</motion.button>;
      })}</motion.div>;
    $[38] = groups;
    $[39] = selectedGroup;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== filteredItems) {
    t20 = filteredItems.length > 0 ? <div className="container mx-auto columns-1 gap-4 space-y-4 sm:columns-2 lg:columns-3 xl:columns-4">{filteredItems.map((item_0, index_0) => <motion.div key={index_0} className="group relative cursor-zoom-in break-inside-avoid overflow-hidden rounded-xl" onClick={() => setSelectedIndex(index_0)} initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        y: 0,
        scale: 1
      }} viewport={{
        once: true,
        margin: "-50px"
      }} transition={{
        duration: 0.6,
        delay: index_0 * 0.1,
        ease: [0.22, 1, 0.36, 1] as const
      }} whileHover={{
        scale: 1.02
      }}><LazyImage src={item_0.image?.src ?? ""} alt={item_0.image?.alt ?? ""} className="h-auto w-full transition-transform duration-300 group-hover:scale-105" sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw" /><div className="absolute inset-0 flex flex-col justify-end bg-black/60 p-6 opacity-0 transition-opacity duration-300 group-hover:opacity-100"><h3 className="mb-2 translate-y-4 text-sm font-medium text-white transition-transform duration-300 group-hover:translate-y-0">{item_0.title}</h3>{(item_0 as any).button && <div className="mt-3 translate-y-4 transition-transform delay-100 duration-300 group-hover:translate-y-0" onClick={_temp2}><Button asChild={true} variant={(item_0 as any).button.variant || "default"} size={(item_0 as any).button.size || "sm"} className="bg-primary hover:bg-primary/90 text-primary-foreground h-8 w-full border-0 px-1 py-1.5 text-sm font-medium"><Link href={(item_0 as any).button.url || ""} target={(item_0 as any).button.target || "_self"}>{(item_0 as any).button.icon && <SmartIcon name={(item_0 as any).button.icon as string} />}{(item_0 as any).button.title}</Link></Button></div>}</div></motion.div>)}</div> : <motion.div className="text-muted-foreground container text-center" initial={{
      opacity: 0
    }} whileInView={{
      opacity: 1
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.4
    }}>No items found in this category.</motion.div>;
    $[41] = filteredItems;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== filteredItems || $[44] !== handleNext || $[45] !== handlePrevious || $[46] !== selectedIndex) {
    t21 = selectedIndex !== null && filteredItems && filteredItems.length > 0 && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm md:p-8" onClick={() => setSelectedIndex(null)}><button className="absolute top-4 right-4 z-50 text-white/70 transition-colors hover:text-white" onClick={() => setSelectedIndex(null)}><X className="size-8" /></button><button className="absolute top-1/2 left-4 z-50 -translate-y-1/2 rounded-full bg-black/20 p-2 text-white/70 transition-colors hover:bg-black/40 hover:text-white" onClick={e_1 => {
        e_1.stopPropagation();
        handlePrevious();
      }}><ChevronLeft className="size-8 md:size-12" /></button><button className="absolute top-1/2 right-4 z-50 -translate-y-1/2 rounded-full bg-black/20 p-2 text-white/70 transition-colors hover:bg-black/40 hover:text-white" onClick={e_2 => {
        e_2.stopPropagation();
        handleNext();
      }}><ChevronRight className="size-8 md:size-12" /></button><motion.div key={selectedIndex} initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="relative flex h-full w-full items-center justify-center" onClick={_temp3}><div className="relative max-h-full max-w-full overflow-hidden rounded-lg"><LazyImage src={filteredItems[selectedIndex].image?.src ?? ""} alt={filteredItems[selectedIndex].image?.alt ?? ""} className="h-auto max-h-[90vh] w-auto max-w-full object-contain" /><div className="absolute right-0 bottom-0 left-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6 text-white"><h3 className="mb-2 text-2xl font-bold">{filteredItems[selectedIndex].title}</h3>{filteredItems[selectedIndex].description && <p className="line-clamp-3 text-base text-white/90">{filteredItems[selectedIndex].description}</p>}{(filteredItems[selectedIndex] as any).button && <div className="mt-4"><Button asChild={true} variant={(filteredItems[selectedIndex] as any).button.variant || "default"} size={(filteredItems[selectedIndex] as any).button.size || "default"} className="bg-primary hover:bg-primary/90 h-8 border-0 px-3 py-1.5 text-sm font-medium text-white"><Link href={(filteredItems[selectedIndex] as any).button.url || ""} target={(filteredItems[selectedIndex] as any).button.target || "_self"}>{(filteredItems[selectedIndex] as any).button.icon && <SmartIcon name={(filteredItems[selectedIndex] as any).button.icon as string} className="text-white" />}{(filteredItems[selectedIndex] as any).button.title}</Link></Button></div>}</div></div></motion.div></motion.div>;
    $[43] = filteredItems;
    $[44] = handleNext;
    $[45] = handlePrevious;
    $[46] = selectedIndex;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t21) {
    t22 = <AnimatePresence>{t21}</AnimatePresence>;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t18 || $[51] !== t19 || $[52] !== t20 || $[53] !== t22 || $[54] !== t7 || $[55] !== t8) {
    t23 = <section id={t7} className={t8}>{t18}{t19}{t20}{t22}</section>;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
    $[53] = t22;
    $[54] = t7;
    $[55] = t8;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  return t23;
}
function _temp3(e_3) {
  return e_3.stopPropagation();
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp(button) {
  return <Button key={button.title} variant={button.variant || "default"} size={button.size || "sm"} asChild={true}><Link href={button.url || ""} target={button.target || "_self"}>{button.icon && <SmartIcon name={button.icon as string} />}{button.title}</Link></Button>;
}